import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import { KTSVG, toAbsoluteUrl } from '../../../../../_metronic/helpers'
import { useIntl } from 'react-intl'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import { Table, Pagination, Dropdown } from 'react-bootstrap'
import { DisplayImage } from '../../../widgets/components/General/DisplayImage'
import View from '../../assets/icons/ic-view.svg'
import Download from '../../assets/icons/ic-download.svg'
import File from '../../assets/icons/ic-file.svg'
import LocalFile from '../../assets/icons/ic-localupload.svg'
import Drive from '../../assets/icons/ic-drive.svg'
import Collaboration from '../../assets/icons/ic-dropbox.svg'
import Amazon from '../../assets/icons/ic-amazon-s3.svg'
import { DataTableFilterSearchFilter } from './DataTableFilterSearchFilter'
import { useAuth } from '../../../auth'
import {
  createAccess,
  createActivityLog,
  getAllFiles,
  getMetaData,
  getViewAndDownloadFile,
} from '../../core/_requests'
import { useTimeZone } from '../../../../hooks/useTimeZone'
import { useDateFormat } from '../../../../hooks/useDateFormat'
import { DateTime } from 'luxon'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getLocation } from '../../../onboarding/core/_requests'
import ViewDocModal from '../../../stakeholders/stakeholders-and-investors/components/individual/view-doc-modal'
import NoDataPage from '../../../widgets/components/NoDataPage'
import SkeletonRow from '../../../digital-signing/components/SkeletonRow'
import threeDots from '../../assets/icons/threeDots.svg';
// import ThreadTitle from '../../../AI-banker/components/shortenString'

declare module '@tanstack/react-table' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>
  }
  interface FilterMeta {
    itemRank: RankingInfo
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({ itemRank })
  return itemRank.passed
}

const SearchAndFiltersDataTable = () => {
  const { formatMessage } = useIntl()
  const [searchAndFilter, setSearchAndFilter] = useState(false)
  const [, setClear] = useState(true) //eslint-disable-line
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false)
  const navigate = useNavigate()
  const [pagination, setPagination] = useState<{
    currentPage: number
    pageSize: number
    totalCount: number
    nextPage: number
    prevPage: number | null
    totalPages: number
  }>({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    nextPage: 0,
    prevPage: null,
    totalPages: 0,
  })
  const [fileNameSearch, setFileNameSearch] = useState<string>('')
  const [sourceNameSearch, setSourceNameSearch] = useState<string>('')
  const [sourceTypeSearch, setSourceTypeSearch] = useState<string>('')
  const [fileTypeSearch, setFileTypeSearch] = useState<string>('')
  const [maxSize, setMaxSize] = useState<any>()
  const [minSize, setMinSize] = useState<any>()
  const [uploadedAfter, setUploadedAfter] = useState<string>('')
  const [uploadedBefore, setUploadedBefore] = useState<string>('')
  const [uploadedBy, setUploadedBy] = useState<any>('')
  const { companyId, currentUser } = useAuth()
  const [allFiles, setAllFiles] = useState<any[]>([])
  const { getTimeZoneValue } = useTimeZone()
  const { getDateValue } = useDateFormat()
  const getAllFilteredFiles = async () => {
    try {
      setIsLoading(true)
      const {
        data: { success, data },
      } = await getAllFiles(
        +companyId!,
        pagination.currentPage,
        pagination.pageSize,
        globalFilter || fileNameSearch,
        sourceNameSearch,
        sourceTypeSearch,
        fileTypeSearch,
        +maxSize!,
        +minSize!,
        uploadedAfter,
        uploadedBefore,
        +uploadedBy
      )
      if (success) {
        setAllFiles(data.data)
        setPagination((prev) => ({
          ...prev,
          currentPage: data.pagination.currentPage,
          pageSize: data.pagination.pageSize,
          totalCount: data.pagination.totalRecords,
          nextPage: data.pagination.nextPage,
          prevPage: data.pagination.prevPage,
          totalPages: data.pagination.totalPages,
        }))
      }
    } catch (err) {
      console.error('Error fetching folder or file list:', err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getAllFilteredFiles()
  }, [pagination.currentPage, pagination.pageSize, globalFilter, companyId, sourceNameSearch, sourceTypeSearch, fileTypeSearch, maxSize, minSize, uploadedAfter, uploadedBefore, uploadedBy]) // eslint-disable-line react-hooks/exhaustive-deps

  const parseFileSize = (sizeStr: string): number => {
    if (!sizeStr) return 0

    const match = sizeStr
      .trim()
      .toLowerCase()
      .match(/^([\d.]+)\s*(kb|mb|gb|b)?$/)
    if (!match) return 0

    const value = parseFloat(match[1])
    const unit = match[2]

    switch (unit) {
      case 'b':
        return value
      case 'kb':
        return value * 1024
      case 'mb':
        return value * 1024 * 1024
      case 'gb':
        return value * 1024 * 1024 * 1024
      default:
        return value
    }
  }
  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        accessorFn: (row) => row.fileVersion?.fileName || '',
        id: 'fileName',
        header: 'File Name',
        cell: (info) => {
          const fileType = info.row.original?.fileVersion?.fileType || ''
          return (
            <div className='d-flex align-items-center gap-2'>
              {fileType === 'pdf' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/pdf.svg')} alt='pdf' />
              )}
              {fileType === 'ppt' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/ppt.svg')} alt='ppt' />
              )}
              {fileType === 'pptx' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/pptx.svg')} alt='pptx' />
              )}
              {fileType === 'txt' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/txt.svg')} alt='txt' />
              )}
              {fileType === 'doc' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/doc.svg')} alt='doc' />
              )}
              {fileType === 'docx' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/docx.svg')} alt='docx' />
              )}
              {fileType === 'xml' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/xml.svg')} alt='xml' />
              )}
              {fileType === 'xls' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/xls.svg')} alt='xls' />
              )}
              {fileType === 'xlsx' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/xlsx.svg')} alt='xlsx' />
              )}
              {fileType === 'svg' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/svg.svg')} alt='svg' />
              )}
              {fileType === 'png' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/png.svg')} alt='png' />
              )}
              {fileType === 'jpg' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/jpg.svg')} alt='jpg' />
              )}
              {fileType === 'jpeg' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/jpeg.svg')} alt='jpeg' />
              )}
              {fileType === 'avi' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/avi.svg')} alt='avi' />
              )}
              {fileType === 'file' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/file.svg')} alt='file' />
              )}
              {fileType === 'gif' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/gif.svg')} alt='gif' />
              )}
              {fileType === 'html' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/html.svg')} alt='html' />
              )}
              {fileType === 'csv' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/csv.svg')} alt='csv' />
              )}
              {fileType === 'm4a' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/m4a.svg')} alt='m4a' />
              )}
              {fileType === 'mov' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/mov.svg')} alt='mov' />
              )}
              {fileType === 'mkv' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/mkv.svg')} alt='mkv' />
              )}
              {fileType === 'mp3' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/mp3.svg')} alt='mp3' />
              )}
              {fileType === 'mp4' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/mp4.svg')} alt='mp4' />
              )}
              {fileType === 'note' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/note.svg')} alt='note' />
              )}
              {fileType === 'odt' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/odt.svg')} alt='odt' />
              )}
              {fileType === 'odp' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/odp.svg')} alt='odp' />
              )}
              {fileType === 'ods' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/ods.svg')} alt='ods' />
              )}
              {fileType === 'ogg' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/ogg.svg')} alt='ogg' />
              )}
              {fileType === 'rtf' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/rtf.svg')} alt='rtf' />
              )}
              {fileType === 'tif' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/tif.svg')} alt='tif' />
              )}
              {fileType === 'tiff' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/tiff.svg')} alt='tiff' />
              )}
              {fileType === 'wav' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/wav.svg')} alt='wav' />
              )}
              {fileType === 'webp' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/webp.svg')} alt='webp' />
              )}
              {fileType === 'wma' && (
                <img src={toAbsoluteUrl('/media/icons/file_extensions/wma.svg')} alt='wma' />
              )}
              {fileType === 'wmv' && (
                <img
                  src={toAbsoluteUrl('/media/icons/file_extensions/vid-generic.svg')}
                  alt='wmv'
                />
              )}
              {fileType === 'mpg' && (
                <img
                  src={toAbsoluteUrl('/media/icons/file_extensions/vid-generic.svg')}
                  alt='mpg'
                />
              )}
              {fileType === 'webm' && (
                <img
                  src={toAbsoluteUrl('/media/icons/file_extensions/vid-generic.svg')}
                  alt='webm'
                />
              )}
              {fileType === 'flac' && (
                <img
                  src={toAbsoluteUrl('/media/icons/file_extensions/generic-audio.svg')}
                  alt='flac'
                />
              )}
              {fileType === 'aiff' && (
                <img
                  src={toAbsoluteUrl('/media/icons/file_extensions/generic-audio.svg')}
                  alt='aiff'
                />
              )}
              {fileType === 'mid' && (
                <img
                  src={toAbsoluteUrl('/media/icons/file_extensions/generic-audio.svg')}
                  alt='mid'
                />
              )}
              {fileType === 'snd' && (
                <img
                  src={toAbsoluteUrl('/media/icons/file_extensions/generic-audio.svg')}
                  alt='snd'
                />
              )}
              <div className='cursor-pointer'>
                <h5 className='m-0 fw-normal font-size-13'>{info.getValue()}</h5>
              </div>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorFn: (row) => row.fileVersion?.fileSize || '0 MB',
        id: 'fileSize',
        header: 'File Size',
        cell: (info) => info.getValue(), // Still display as-is
        sortingFn: (rowA, rowB, columnId) => {
          const sizeA = parseFileSize(rowA.getValue(columnId))
          const sizeB = parseFileSize(rowB.getValue(columnId))
          return sizeA - sizeB
        },
        filterFn: 'includesString',
      },
      {
        accessorFn: (row) => {
          const user = row.createdBy
          return user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : ''
        },
        id: 'uploadedBy',
        header: 'Uploaded By',
        cell: (info) => {
          const fullName = info.getValue()
          return (
            <div className='d-flex align-items-center gap-2'>
              <DisplayImage
                imgName={''}
                alt={'profile-image'}
                height={20}
                width={20}
                className='rounded-circle'
              />
              <div>
                <h5 className='m-0 fw-normal font-size-13'>{fullName || '-'}</h5>
              </div>
            </div>
          )
        },
        filterFn: 'includesString',
        sortingFn: (rowA, rowB, columnId) => {
          const nameA = rowA.getValue<string>(columnId)?.toLowerCase() ?? ''
          const nameB = rowB.getValue<string>(columnId)?.toLowerCase() ?? ''
          return nameA.localeCompare(nameB)
        },
      },
      {
        accessorKey: 'createdAt',
        id: 'createdAt',
        header: 'Uploaded On',
        cell: (info) => {
          const dateString = (info.getValue() as string)?.toString()
          return (
            <>
              {dateString
                ? getDateValue(dateString)
                  ? getDateValue(dateString)
                  : DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_MED)
                : '-'}{' '}
              {dateString && getTimeZoneValue(dateString)}
            </>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorFn: (row) => row.source?.storageName || '',
        id: 'SourceName',
        header: 'Source Name',
        cell: (info) => {
          const sourceType = info.row.original?.source?.cloudType || ''
          console.log(sourceType, 'sourceType')
          return (
            <div className='d-flex align-items-center gap-2'>
              {sourceType === 'Local' && <img src={LocalFile} alt='Local-File' />}
              {sourceType === 'S3' && <img src={Amazon} height={20} width={20} alt='S3' />}
              {sourceType === 'DropBox' && <img src={Collaboration} alt='DropBox' />}
              {sourceType === 'GDrive' && <img src={Drive} alt='GDrive' />}
              {sourceType === 'ZipFile' && <img width={20} height={20} src={toAbsoluteUrl('/media/icons/file_extensions/ZIP.svg')} alt='zip' />}
              <div className='text-wrap' style={{ maxWidth: '250px' }}>
                <h5 className='m-0 fw-normal font-size-13'>
                  {/* <ThreadTitle title={info.row.original?.source?.storageName} /> */}
                  {info.row.original?.source?.storageName}
                </h5>
              </div>
            </div>
          )
        },
        filterFn: 'includesString',
      },
    ], []) // eslint-disable-line react-hooks/exhaustive-deps

  const table = useReactTable({
    data: allFiles as any,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    manualPagination: true,
    rowCount: pagination.totalCount,
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    // globalFilterFn: multiFieldGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const handlePageChange = (pageNumber: number) => {
    setPagination((prev) => ({ ...prev, currentPage: pageNumber }))
  }
  const [metaData, setMetaData] = useState<any>()
  const [isActive, setIsActive] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [metadataLoading, setMetaDataLoading] = useState<boolean>(false)

  const [viewableData, setViewableData] = useState<
    { downloadUrl: string; fileName: string; extension: string } | {}
  >({})
  const [selectedVersion, setSelectedVersion] = useState('')

  const getAllMetaData = async (
    recordId: string,
    isExplorer?: boolean | null,
    isDownload?: boolean
  ) => {
    if (!companyId) return
    try {
      setIsLoading(true)
      setMetaDataLoading(true)
      const {
        data: { data, success, errors },
      } = await getMetaData(companyId, recordId, selectedVersion)
      if (success) {
        setMetaData(data)
        setSelectedVersion(data.metadata.fileVersionId)
        if (isExplorer) {
          navigate(
            `/rk/records-and-files/${encodeURIComponent(
              data?.metadata?.structureClassificationLevelOne.name
            )}/${encodeURIComponent(
              data?.metadata?.structureClassificationLevelTwo.name
            )}/${encodeURIComponent(data?.metadata?.structureClassificationLevelThree.name)}`
          )
        }
        if (isDownload === false || isDownload === true) {
          handleViewAndDownloadFile(isDownload)
        }
      } else {
        toast.error(errors[0])
      }
    } catch (err) {
    } finally {
      setMetaDataLoading(false)
      setIsLoading(false)
    }
  }

  const getDeviceType = () => {
    const ua = navigator.userAgent

    if (
      /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera Mobi|Blazer|Dolfin|Dolphin|Skyfire|Zune/.test(
        ua
      )
    ) {
      return 'MOBILE'
    }
    return 'WEB'
  }

  const getOS = () => {
    const ua = navigator.userAgent
    if (/iPhone|iPad|iPod|Macintosh/.test(ua)) return 'APPLE'
    if (/Win/.test(ua)) return 'WINDOWS'
    if (/android/i.test(ua)) return 'ANDROID'
    return 'Unknown'
  }
  const createActivity = async (recordId: any) => {
    const os = getOS()
    const deviceTypeView = getDeviceType()
    const locationApiData = await getLocation()
    const city = locationApiData?.data.country_name
    const ipAddress = locationApiData?.data?.ip
    try {
      if (deviceTypeView && os && city && recordId && companyId) {
        const {
          data: { success, errors },
        } = await createActivityLog(
          companyId,
          recordId,
          os,
          deviceTypeView,
          city,
          ipAddress,
          'VIEWED'
        )
        if (!success) {
          errors.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
        }
      }
    } catch (err) {
      console.log(err)
    }
  }
  const createAccessLog = async (recordId: any) => {
    const os = getOS()
    const deviceTypeView = getDeviceType()
    const locationApiData = await getLocation()
    const city = locationApiData?.data.country_name
    const ipAddress = locationApiData?.data?.ip

    try {
      if (deviceTypeView && os && city && recordId && companyId && currentUser?.userId) {
        const {
          data: { success, errors },
        } = await createAccess(
          recordId,
          os,
          deviceTypeView,
          city,
          ipAddress,
          'CREATE',
          companyId,
          currentUser?.userId
        )
        if (!success) {
          errors.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
        }
      }
    } catch (err) {
      console.log(err)
    }
  }

  const handleViewAndDownloadFile = async (isDownload: boolean) => {
    if (!companyId) return
    if (!isDownload) setIsActive(true)
    try {
      const {
        data: { data, success },
      } = await getViewAndDownloadFile(metaData?.metadata?.fileVersionId, companyId, isDownload)
      if (success) {
        if (isDownload) {
          const link = document.createElement('a')
          link.href = data.downloadUrl
          link.setAttribute('download', metaData?.metadata?.fileName || 'downloaded-file') // Ensure a valid filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          toast.success(`${metaData?.metadata?.fileName} downloaded successfully`)
        } else {
          setViewableData(data)
          createActivity(metaData?.metadata?.recordId)
          createAccessLog(metaData?.metadata?.recordId)
        }
      }
    } catch (error) {
      toast.error('Failed to download document')
    }
  }

  return (
    <>
      <div className='d-flex flex-md-row flex-column justify-content-between align-items-md-center gap-2 mb-4'>
        {/* left side table header  */}
        <h1 className='font-size-16 mb-0'>{formatMessage({ id: 'Search and Filters' })}</h1>
        {/* right side table header  */}
        <div className='d-flex gap-md-2 gap-2'>
          {/* search input */}
          <form className='align-items-center w-100 w-md-auto d-flex height-36'>
            <span className='svg-icon svg-icon-3 position-absolute ms-3'>
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className='mh-50px'
              >
                <rect
                  opacity='0.5'
                  x='17.0365'
                  y='15.1223'
                  width='8.15546'
                  height='2'
                  rx='1'
                  transform='rotate(45 17.0365 15.1223)'
                  fill='currentColor'
                ></rect>
                <path
                  d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                  fill='currentColor'
                ></path>
              </svg>
            </span>
            <input
              className='bg-body form-control form-control-flush ps-10 rounded-2 font-size-13 font-weight-400 height-36'
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder='Search Files'
            />
          </form>

          {/* Filter Btn  */}
          <div
            className={`h-100  d-flex rounded ${isFilterApplied ? 'bg-success' : 'bg-white'}`}
          >
            <button
              className={clsx(
                'text-clr88 btn btn-sm btn-flex fw-semibold py-2 font-size-12 height-36'
              )}
              id='fc-toogle'
              title='Sort and Filter'
              data-bs-toggle='tooltip'
              data-bs-placement='left'
              data-bs-dismiss='click'
              data-bs-trigger='hover'
              onClick={() => {
                setSearchAndFilter(true)
              }}
            >
              <KTSVG
                path={toAbsoluteUrl('/media/icons/duotune/general/gen031.svg')}
                className={`svg-icon-6 me-1 ${isFilterApplied ? 'svg-icon-white' : 'svg-icon-muted'}`}
              />
              <span className={`d-none d-md-block ${isFilterApplied ? 'text-white' : 'text-muted'}`}>
                {formatMessage({ id: 'Filter' })}
                {isFilterApplied ? '*' : ''}
              </span>
              {/* {selectedShareholderTypeFilter || selectedShareholderSortTypeFilter ? '*' : ''} */}
            </button>
          </div>
          {/* Kebab menu  */}
          <div className='d-flex align-items-center position-relative height-36'>
            <div className='dropdown-export-container'>
              <Dropdown>
                <Dropdown.Toggle className='btn btn-white table-three-dots dots-container'>
                  <img src={threeDots} alt='' className='three-dots-image' />
                </Dropdown.Toggle>

                <Dropdown.Menu className='m-1 rounded'>
                  <Dropdown.Item
                    className='text-dark ps-5 rounded'
                    onClick={() => {
                      // exportToCsv(tabKey)
                    }}
                  >
                    {formatMessage({ id: 'Export to CSV' })}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

        </div>
      </div>
      <div className='bg-white rounded-top p-4' style={{ overflowX: 'auto' }}>
        {!isLoading && (allFiles?.length === 0 || table.getRowModel().rows.length === 0) ? (
          <div className='my-5 mx-6'>
            <NoDataPage width={'w-0'} bgTransparent />
          </div>
        ) : (
          <Table className='w-100 mb-0' style={{ minWidth: '600px' }}>
            <thead className='custom-thead border-bottom'>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className='font-size-12 font-weight-400 table-row border-dark-mode'
                >
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className='min-w-120px pb-6 pt-0 px-4'
                    // style={{ minWidth: '120px' }}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                  <th className='min-w-120px pb-6 pt-4 px-4 text-end'>
                    {formatMessage({ id: 'Actions' })}
                  </th>
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                <>
                  <SkeletonRow count={10} />
                </>
              ) : (
                // table.getRowModel().rows.map((row, index) => (
                //   <React.Fragment key={row.id}>
                //     <tr key={row.id} className='border-bottom'>
                //       {row.getVisibleCells().map((cell, cellIndex) => (
                //         <>
                //           {cellIndex === 0 ? (
                //             <td key={cell.id} className='pt-5 px-4 border-0'>
                //               <div className='d-flex gap-2'>
                //                 {flexRender(cell.column.columnDef.cell, cell.getContext())}
                //               </div>
                //             </td>
                //           ) : (
                //             <td key={cell.id} className='pt-6 px-4 border-0'>
                //               {flexRender(cell.column.columnDef.cell, cell.getContext())}
                //             </td>
                //           )}
                //         </>
                //       ))}
                //       <td className='pt-4 px-4' onClick={(e) => e.stopPropagation()}>
                //         <div className='d-flex gap-2 align-items-center justify-content-end'>
                //           <img
                //             src={File}
                //             alt='file-icon'
                //             className='cursor-pointer'
                //             onClick={() =>
                //               navigate(
                //                 `/rk/records-and-files/files-details/${row.original.recordId}`
                //               )
                //             }
                //           />
                //           <img
                //             src={View}
                //             alt='view-icon'
                //             className='cursor-pointer'
                //             onClick={() => {
                //               getAllMetaData(row.original.recordId)
                //               if (!metadataLoading) {
                //                 handleViewAndDownloadFile(false)
                //               }
                //             }}
                //           />
                //           <img
                //             src={Download}
                //             alt='download-icon'
                //             className='cursor-pointer'
                //             onClick={() => {
                //               getAllMetaData(row.original.recordId)
                //               if (!metadataLoading) {
                //                 handleViewAndDownloadFile(true)
                //               }
                //             }}
                //           />

                //           <p
                //             className='m-0 cursor-pointer'
                //             aria-disabled={metadataLoading}
                //             onClick={() => {
                //               setMetaDataLoading(true)
                //               getAllMetaData(row.original.recordId, true)
                //             }}
                //           >
                //             {metadataLoading ? (
                //               'Please wait ...'
                //             ) : (
                //               <i className='fa-solid fa-arrow-up-right-from-square ms-1 cursor-pointer'></i>
                //             )}
                //           </p>
                //         </div>
                //       </td>
                //     </tr>
                //   </React.Fragment>
                // ))
                table.getRowModel().rows.map((row, index) => (
                  <React.Fragment key={row.id}>
                    <tr key={row.id} className='border-bottom'>
                      {row.getVisibleCells().map((cell) => {
                        const isFileNameColumn = cell.column.id === 'fileName'
                        return (
                          <td
                            key={cell.id}
                            className={`px-4 border-0 text-wrap ${isFileNameColumn ? 'pt-5' : 'pt-6'} ${isFileNameColumn ? 'cursor-pointer' : ''
                              }`}
                            style={{ minWidth: '120px', maxWidth: '300px' }}
                            onClick={() => {
                              if (isFileNameColumn) {
                                getAllMetaData(row.original.recordId, null, false)
                              }
                            }}
                          >
                            {isFileNameColumn ? (
                              <div className='d-flex gap-2'>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </div>
                            ) : (
                              <>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </>
                            )}
                          </td>
                        )
                      })}

                      <td className='pt-4 px-4' onClick={(e) => e.stopPropagation()}>
                        <div className='d-flex gap-2 align-items-center justify-content-end'>
                          <img
                            src={File}
                            alt='file-icon'
                            className='cursor-pointer'
                            onClick={() =>
                              navigate(
                                `/rk/records-and-files/files-details/${row.original.recordId}`
                              )
                            }
                          />
                          <img
                            src={View}
                            alt='view-icon'
                            className='cursor-pointer'
                            onClick={() => {
                              getAllMetaData(row.original.recordId)
                              if (!metadataLoading) {
                                handleViewAndDownloadFile(false)
                              }
                            }}
                          />
                          <img
                            src={Download}
                            alt='download-icon'
                            className='cursor-pointer'
                            onClick={() => {
                              getAllMetaData(row.original.recordId, null, true)
                            }}
                          />
                          <p
                            className='m-0 cursor-pointer'
                            aria-disabled={metadataLoading}
                            onClick={() => {
                              setMetaDataLoading(true)
                              getAllMetaData(row.original.recordId, true)
                            }}
                          >
                            {metadataLoading ? (
                              'Please wait ...'
                            ) : (
                              <i className='fa-solid fa-arrow-up-right-from-square ms-1 cursor-pointer'></i>
                            )}
                          </p>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </Table>
        )}
        <ViewDocModal
          isActive={isActive}
          setIsActive={setIsActive}
          fileName={metaData?.metadata?.fileName}
          isRecordKeeper={true}
          isLoading={isLoading}
          viewableData={viewableData}
        />
      </div>
      <div className='w-100 d-md-flex justify-content-between align-items-center px-4 py-4 bg-white rounded-bottom mb-8'>
        {allFiles?.length > 0 && (
          <div className='w-100 d-flex flex-md-row flex-column justify-content-between align-items-center px-0 mt-8'>
            <div className='d-flex gap-2 w-100 align-items-center justify-content-md-start justify-content-center mb-4'>
              <select
                aria-label='Default select example'
                className={`p-4 py-3 w-80px border border-1 rounded-2 font-size-13 bg-white text-dark`}
                value={pagination.pageSize}
                onChange={(e) => {
                  const newSize = Number(e.target.value)
                  setPagination((prev) => ({
                    ...prev,
                    pageSize: newSize,
                    pageIndex: 0, // Reset to first page when page size changes
                  }))
                }}
              >
                {[10, 20, 50, 100].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>

              <span className='font-size-13 text-center text-md-start'>
                {formatMessage({
                  id: `Showing ${pagination.currentPage === 1
                    ? 1
                    : (pagination.currentPage - 1) * pagination.pageSize + 1
                    } to ${pagination.nextPage ? allFiles.length : pagination.totalCount} of ${pagination.totalCount
                    } records`,
                })}
              </span>
            </div>

            {/* <Pagination className='w-md-100 w-auto d-flex flex-end'>
              <Pagination.First onClick={() => handlePageChange(1)} disabled={!pagination.prevPage}>
                {'<<'}
              </Pagination.First>

              <Pagination.Prev
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.prevPage}
              >
                {'<'}
              </Pagination.Prev>

              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <Pagination.Item
                  key={i}
                  active={i === pagination.currentPage - 1}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {1}
                </Pagination.Item>
              ))}

              <Pagination.Next
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.nextPage}
              >
                {'>'}
              </Pagination.Next>

              <Pagination.Last
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={!pagination.nextPage}
              >
                {'>>'}
              </Pagination.Last>
            </Pagination> */}
            <Pagination className='w-md-100 w-auto d-flex flex-end'>
              <Pagination.First onClick={() => handlePageChange(1)} disabled={!pagination.prevPage}>
                {'<<'}
              </Pagination.First>

              <Pagination.Prev
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.prevPage}
              >
                {'<'}
              </Pagination.Prev>

              {(() => {
                const items = []
                const { currentPage, totalPages } = pagination
                const pageNeighbours = 1 // number of pages to show on each side of current

                const startPage = Math.max(2, currentPage - pageNeighbours)
                const endPage = Math.min(totalPages - 1, currentPage + pageNeighbours)

                // Always show first page
                items.push(
                  <Pagination.Item
                    key={1}
                    active={currentPage === 1}
                    onClick={() => handlePageChange(1)}
                  >
                    1
                  </Pagination.Item>
                )

                // Show left ellipsis if needed
                if (startPage > 2) {
                  items.push(<Pagination.Ellipsis key='start-ellipsis' disabled />)
                }

                // Show middle page numbers
                for (let i = startPage; i <= endPage; i++) {
                  items.push(
                    <Pagination.Item
                      key={i}
                      active={i === currentPage}
                      onClick={() => handlePageChange(i)}
                    >
                      {i}
                    </Pagination.Item>
                  )
                }

                // Show right ellipsis if needed
                if (endPage < totalPages - 1) {
                  items.push(<Pagination.Ellipsis key='end-ellipsis' disabled />)
                }

                // Always show last page if totalPages > 1
                if (totalPages > 1) {
                  items.push(
                    <Pagination.Item
                      key={totalPages}
                      active={currentPage === totalPages}
                      onClick={() => handlePageChange(totalPages)}
                    >
                      {totalPages}
                    </Pagination.Item>
                  )
                }

                return items
              })()}

              <Pagination.Next
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.nextPage}
              >
                {'>'}
              </Pagination.Next>

              <Pagination.Last
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={!pagination.nextPage}
              >
                {'>>'}
              </Pagination.Last>
            </Pagination>
          </div>
        )}
      </div>
      <DataTableFilterSearchFilter
        filter={searchAndFilter}
        setFilter={setSearchAndFilter}
        setClear={setClear}
        fileNameSearch={fileNameSearch}
        setFileNameSearch={setFileNameSearch}
        setSourceNameSearch={setSourceNameSearch}
        setSourceTypeSearch={setSourceTypeSearch}
        setFileTypeSearch={setFileTypeSearch}
        setMaxSize={setMaxSize}
        setMinSize={setMinSize}
        setUploadedAfter={setUploadedAfter}
        setUploadedBefore={setUploadedBefore}
        setUploadedBy={setUploadedBy}
        setIsFilterApplied={setIsFilterApplied}
      />
    </>
  )
}

export default SearchAndFiltersDataTable
